import type { RouteRecordRaw } from 'vue-router';
import { STUDIO_ADMIN_CRUD, STUDIO_CRUD, STUDIO_FINANCIALS_READ, STUDIO_USERS_CRUD } from '@core/models/permissions';
import { permissionRouteGuard, featureFlagRouteGuard, studioVerificationRouteGuard } from '@core/route-guards';
import { jobEditorLinks } from '@apps/job-editor/routing';
import { routes as organizationEditorLinks } from '@apps/organization-editor/routing';

export const STUDIO_ADMIN_HOME = 'studioadmin-home';
export const STUDIO_ADMIN_PREPAYS = 'studioadmin-prepays';
export const STUDIO_ADMIN_GALLERIES = 'studioadmin-galleries';
export const STUDIO_ADMIN_COUPONS = 'studioadmin-coupons';
export const STUDIO_ADMIN_REPORTS = 'studioadmin-reports';
export const STUDIO_ADMIN_USERS = 'studioadmin-users';
export const STUDIO_ADMIN_SETTINGS = 'studioadmin-settings';
export const STUDIO_ADMIN_PKG_PREFS = 'studioadmin-pkgprefs';
export const STUDIO_ADMIN_PKG_SET_MGMT = 'studioadmin-pkgsetmgmt';
export const STUDIO_ADMIN_PKG_SET_MGMT_PKG = 'studioadmin-pkgsetmgmtpkgs';
export const STUDIO_ADMIN_PKG = 'studioadmin-pkgmgmt';
export const STUDIO_ADMIN_BACKGROUND_SETS = 'studioadmin-backgroundsets';
export const STUDIO_ADMIN_BACKGROUND_SETS_EDIT = 'studioadmin-backgroundsets-edit';
export const STUDIO_ADMIN_PROFILE = 'studioadmin-profile';
export const STUDIO_ADMIN_JOBS = 'studioadmin-jobs';
export const STUDIO_ADMIN_ORGANIZATIONS = 'studioadmin-organizations';
export const STUDIO_ADMIN_ACCESS_TOKENS = 'studioadmin-access-tokens';
export const STUDIO_ADMIN_MARKETING_CAMPAIGNS = 'studioadmin-marketing-campaigns';
export const STUDIO_ADMIN_MARKETING_CAMPAIGN_CREATE = 'studioadmin-marketing-campaign-create';
export const STUDIO_ADMIN_MARKETING_CAMPAIGN_DETAIL = 'studioadmin-marketing-campaign-detail';
export const STUDIO_ADMIN_MARKETING_CAMPAIGN_AUTOMATION = 'studioadmin-marketing-campaign-automation';
export const STUDIO_ADMIN_MARKETING_CAMPAIGN_AUTOMATION_DELIVERY = 'studioadmin-marketing-campaign-automation-delivery';
export const STUDIO_ADMIN_MARKETING_PROMOTION_EDIT = 'studioadmin-marketing-promotion-edit';
export const STUDIO_ADMIN_SENDGRID_SETTINGS = 'studioadmin-sendgrid-settings';
export const STUDIO_ADMIN_PRICE_LEVER_DEMO = 'studioadmin-price-lever-demo';
export const STUDIO_ADMIN_SENDGRID_DEBUG_SCOPES = 'studioadmin-sendgrid-debug-scopes';

const routes: RouteRecordRaw[] = [
  {
    path: '/admin/studios/:studioId',
    component: () => import('./layouts/StudioAdminLayout.vue'),
    beforeEnter: [permissionRouteGuard([STUDIO_CRUD, STUDIO_ADMIN_CRUD])],
    children: [
      {
        path: 'profile',
        name: STUDIO_ADMIN_PROFILE,
        component: () => import('./pages/ProfilePage.vue'),
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
      },
      {
        alias: '',
        path: 'home',
        name: STUDIO_ADMIN_HOME,
        component: () => import('./pages/StudioAdminHomePage.vue'),
      },
      {
        path: 'organizations',
        beforeEnter: [
          featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME),
        ],
        meta: {
          breadCrumb: { label: 'Organizations' },
        },
        children: [
          {
            path: '',
            name: STUDIO_ADMIN_ORGANIZATIONS,
            component: () => import('./pages/OrganizationListingPage.vue'),
          },
          ...organizationEditorLinks,
        ],
      },
      {
        path: 'prepay',
        name: STUDIO_ADMIN_PREPAYS,
        component: () => import('./pages/PrepayListingPage.vue'),
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
      },
      {
        path: 'galleries',
        name: STUDIO_ADMIN_GALLERIES,
        component: () => import('./pages/GalleryListingPage.vue'),
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
      },
      {
        path: 'jobs',
        meta: {
          breadCrumb: { label: 'Jobs' },
        },
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
        children: [
          {
            path: '',
            name: STUDIO_ADMIN_JOBS,
            component: () => import('@apps/studioadmin/pages/StudioJobsPage.vue'),
            beforeEnter: featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          },
          ...jobEditorLinks,
        ],
      },
      {
        path: 'coupon-codes',
        name: STUDIO_ADMIN_COUPONS,
        component: () => import('./pages/StudioCouponCodesPage.vue'),
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
      },
      {
        path: 'price-lever-demo',
        name: STUDIO_ADMIN_PRICE_LEVER_DEMO,
        component: () => import('./pages/PriceLeverDemoPage.vue'),
        beforeEnter: [
          featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME),
        ],
      },
      {
        path: 'sendgrid-settings',
        name: STUDIO_ADMIN_SENDGRID_SETTINGS,
        component: () => import('./pages/SendGridSettingsPage.vue'),
        beforeEnter: [
          featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME),
        ],
      },
      {
        path: 'sendgrid-debug-scopes',
        name: STUDIO_ADMIN_SENDGRID_DEBUG_SCOPES,
        component: () => import('./pages/SendGridDebugScopesPage.vue'),
        beforeEnter: [
          featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME),
        ],
      },
      {
        path: 'marketing-campaigns',
        meta: {
          breadCrumb: { label: 'Marketing Campaigns' },
        },
        beforeEnter: [
          featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME),
        ],
        children: [
          {
            path: '',
            name: STUDIO_ADMIN_MARKETING_CAMPAIGNS,
            component: () => import('./pages/MarketingCampaignListingPage.vue'),
          },
          {
            path: 'create',
            name: STUDIO_ADMIN_MARKETING_CAMPAIGN_CREATE,
            component: () => import('./pages/MarketingCampaignCreatePage.vue'),
          },
          {
            path: ':campaignId',
            name: STUDIO_ADMIN_MARKETING_CAMPAIGN_DETAIL,
            component: () => import('./pages/MarketingCampaignDetailPage.vue'),
          },
          {
            path: ':campaignId/automation',
            name: STUDIO_ADMIN_MARKETING_CAMPAIGN_AUTOMATION,
            component: () => import('@apps/studioadmin/pages/MarketingCampaignAutomationPage.vue'),
          },
          {
            path: ':campaignId/automation/delivery',
            name: STUDIO_ADMIN_MARKETING_CAMPAIGN_AUTOMATION_DELIVERY,
            component: () => import('@apps/studioadmin/pages/MarketingCampaignAutomationDeliveryPage.vue'),
          },
          {
            path: ':campaignId/promotions/:promotionId/edit',
            name: STUDIO_ADMIN_MARKETING_PROMOTION_EDIT,
            component: () => import('@apps/studioadmin/pages/MarketingPromotionEditPage.vue'),
          },
        ],
      },
      {
        path: 'reports',
        name: STUDIO_ADMIN_REPORTS,
        component: () => import('./pages/StudioReportPage.vue'),
        beforeEnter: [
          permissionRouteGuard([STUDIO_CRUD, STUDIO_FINANCIALS_READ]),
          studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME),
        ],
      },
      {
        path: 'settings',
        name: STUDIO_ADMIN_SETTINGS,
        component: () => import('./pages/StudioSettingsPage.vue'),
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
      },
      {
        path: 'pkg-prefs',
        name: STUDIO_ADMIN_PKG_PREFS,
        component: () => import('../package-management/pages/PackagePreferencesPage.vue'),
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
      },
      {
        path: 'pkg-set-mgmt',
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
        children: [
          {
            path: '',
            name: STUDIO_ADMIN_PKG_SET_MGMT,
            component: () => import('../package-management/pages/PackageSetManagementPage.vue'),
            beforeEnter: featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          },
          {
            path: ':packageSetId/packages',
            name: STUDIO_ADMIN_PKG_SET_MGMT_PKG,
            component: () => import('../package-management/pages/PackageManagementPageV3.vue'),
          },
        ],
      },
      {
        path: 'packages',
        name: STUDIO_ADMIN_PKG,
        component: () => import('../package-management/pages/PackageManagementPage.vue'),
        beforeEnter: [studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME)],
      },
      {
        path: 'background-sets',
        beforeEnter: [
          featureFlagRouteGuard('rspoJob', STUDIO_ADMIN_HOME),
          studioVerificationRouteGuard(STUDIO_ADMIN_HOME, STUDIO_ADMIN_HOME),
        ],
        children: [
          {
            path: '',
            name: STUDIO_ADMIN_BACKGROUND_SETS,
            component: () => import('../package-management/pages/BackgroundSetsPage.vue'),
          },
          {
            path: ':backgroundSetId/edit',
            name: STUDIO_ADMIN_BACKGROUND_SETS_EDIT,
            component: () => import('../package-management/pages/BackgroundSetEditorPage.vue'),
          },
        ],
      },
    ],
  },
];

export default routes;
